import type { RouteParams } from './Router';

import React from 'react';

import { routes } from '@/constants/Constants';
import { lazyComponent } from '@/shared/utils/router.util';

const EnrollPropertyConfirmation = React.lazy(
  () => import('../features/EnrollProperty/EnrollConfirmation'),
);
const Home = React.lazy(() => import('../features/Home/Home'));
const PropertyPage = React.lazy(
  () => import('../features/EnrollProperty/EnrollPropertyForm/PropertyPage'),
);
const UnitsOwnedEnroll = React.lazy(() => import('../features/PreEnrollment/UnitsOwnedEnroll'));

const RenterInviteRedirect = React.lazy(() =>
  import('./RenterInviteRedirects').then((mod) => ({ default: mod.RenterInviteRedirect })),
);
const RenterInviteConfirmationRedirect = React.lazy(() =>
  import('./RenterInviteRedirects').then((mod) => ({
    default: mod.RenterInviteConfirmationRedirect,
  })),
);
const MyProperties = React.lazy(() => import('../features/Home/MyProperties'));
const DealsDashboard = React.lazy(() => import('../features/DealsDashboard/DealsDashboard'));
const RenewalCollectPage = React.lazy(
  () => import('../features/RenewalIntent/renewal-intent-collection'),
);
const DealsDashboardRedirect = React.lazy(() => import('../utils/RedirectComponent'));
const { ZgriDashboardRoute } = lazyComponent(
  () => import('../features/zgri/routes/zgri-dashboard.route'),
  'ZgriDashboardRoute',
);

const ClaimsLegacyRedirect = React.lazy(() => import('../utils/RedirectComponent'));
const ClaimsReport = React.lazy(() => import('../features/Claims/components/power-bi-report'));
const ClaimsForm = React.lazy(() => import('../features/Claims/components/submission-form'));
const ClaimsThankYou = React.lazy(
  () => import('../features/Claims/components/submission-form/confirmation'),
);
const ClaimsDashboard = React.lazy(() => import('../features/Claims/components/dashboard'));
const ClaimsSubmissionDetails = React.lazy(
  () => import('../features/Claims/components/dashboard/submission-details'),
);

const CompanyUsers = React.lazy(() => import('../features/SSUM/CompanyUsers'));
const CreateUser = React.lazy(() => import('../features/SSUM/CreateUser'));
const UserDetails = React.lazy(() => import('../features/SSUM/UserDetails'));
const UserPropertiesAssignment = React.lazy(
  () => import('../features/SSUM/UserDetails/PropertiesAssignment'),
);

export const PrivateRoutes: RouteParams[] = (
  [
    {
      path: routes.home,
      component: Home,
      title: 'TheGuarantors',
    },
    {
      path: routes.myProperties,
      component: MyProperties,
      title: 'Your dashboard | TheGuarantors',
    },
    // Renter Invite — legacy routes redirect to App Tracker (drawer is the single entry point)
    {
      path: routes.renterInvite,
      component: RenterInviteRedirect,
      title: 'Invite an applicant | TheGuarantors',
    },
    {
      path: routes.renterInviteConfirmation,
      component: RenterInviteConfirmationRedirect,
      title: 'Applicant invited | TheGuarantors',
    },
    // Property Enrollment
    {
      path: routes.enroll,
      component: PropertyPage,
      title: 'Enroll your property | TheGuarantors',
    },
    {
      path: routes.propertyEnrollmentForm,
      component: PropertyPage,
      title: 'Add a property | TheGuarantors',
    },
    {
      path: routes.unitsOwnedEnroll,
      component: UnitsOwnedEnroll,
      title: 'Complete your enrollment | TheGuarantors',
    },
    {
      path: routes.propertyEnrollConfirm,
      component: EnrollPropertyConfirmation,
      title: 'Property enrolled | TheGuarantors',
    },
    // Deals Dashboard
    {
      path: routes.dealsDashboard,
      component: DealsDashboard,
      title: 'Application Tracker | TheGuarantors',
    },
    {
      path: routes.renewalIntentCollect,
      component: RenewalCollectPage,
      title: 'Confirm lease renewal | TheGuarantors',
      handle: {
        hasBreadcrumbs: true,
        hideFooter: true,
      },
    },
    // Deals Dashboard Old Route to Redirect
    {
      path: routes.dealsDashboardRedirectLink,
      component: DealsDashboardRedirect,
      title: 'Application Tracker | TheGuarantors',
    },
    // Renters Insurance
    {
      path: routes.rentersInsurance,
      component: ZgriDashboardRoute,
      title: 'Renters Insurance | TheGuarantors',
    },
    {
      path: routes.claimsDashboard,
      component: ClaimsDashboard,
      title: 'Claims Dashboard | TheGuarantors',
    },
    {
      path: routes.claimsForm,
      component: ClaimsForm,
      title: 'File a Claim | TheGuarantors',
      handle: {
        hasBreadcrumbs: true,
      },
    },
    {
      path: routes.claimsFormConfirmation,
      component: ClaimsThankYou,
      title: 'Thank You | TheGuarantors',
    },
    {
      path: routes.claimsSubmissionDetails,
      component: ClaimsSubmissionDetails,
      title: 'Claim Submission | TheGuarantors',
      handle: {
        hasBreadcrumbs: true,
      },
    },
    {
      path: routes.claimsReport,
      component: ClaimsReport,
      title: 'Claims Report | TheGuarantors',
    },
    // Claims legacy redirect: Power BI report
    {
      path: '/track-claims',
      component: ClaimsLegacyRedirect,
      title: 'Claims Report | TheGuarantors',
    },
    // Claims legacy redirect: form instructions
    {
      path: '/claims/instructions',
      component: ClaimsLegacyRedirect,
      title: 'File a Claim | TheGuarantors',
    },
    {
      path: routes.users,
      component: CompanyUsers,
      title: 'Company Users | TheGuarantors',
    },
    {
      path: routes.createUser,
      component: CreateUser,
      title: 'Create User | TheGuarantors',
      handle: {
        hasBreadcrumbs: true,
      },
    },
    {
      path: routes.userDetails,
      component: UserDetails,
      title: 'User Details | TheGuarantors',
      handle: {
        hasBreadcrumbs: true,
      },
    },
    {
      path: routes.userPropertiesAssignment,
      component: UserPropertiesAssignment,
      title: 'Assign Properties | TheGuarantors',
      handle: {
        layout: 'split-view',
        hasBreadcrumbs: true,
        hideFooter: true,
      },
    },
  ] as Omit<RouteParams, 'role'>[]
).map((route) => {
  return {
    ...route,
    role: 'protected',
  };
});
